import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Pagination from "@amzn/awsui-components-console/pagination";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Box from "@amzn/meridian/esm/components/box";
import SummaryCard from "../../common/summary/SummaryCard";
import { Theme } from "@amzn/meridian";
import Loading from "../../common/Loading";
import NoSummary from "../../inMeeting/././summary/NoSummary";
import { fetchPaginatedSummaries, generateSummaryGroup, preProcessInClient } from "../../utils/SummaryUtil";
import { sampleSummaryList } from "../../../localtest/LocalhostTest";
import LogManager from "../../utils/LogManager";
import CommonErrorModal from "../../common/error/CommonErrorModal";
import aimsTheme from "../../../meridian-theme-aims.json";

/**
 * SummariesTabInClient renders a paginated view of meeting summaries in the Zoom client.
 *
 * Behavior:
 * - Fetches summaries from the backend using `fetchSummaries`.
 * - Summaries are grouped by date (e.g., "Today", "Yesterday") using `generateSummaryGroup` or `preProcessInMeeting`.
 * - Each group is paginated into chunks of 4 summaries via `preProcessInClient`.
 * - Displays loading, error, or empty states as appropriate.
 *
 * UI:
 * - Displays a header box with the current group title.
 * - Renders 2x2 summary cards per page.
 * - Provides pagination controls to navigate between pages.
 *
 * Used in: AIMS Zoom app when user clicks the "Summaries" tab from the client
 */
const SummariesTabInClient = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [summaryGroups, setSummaryGroups] = useState(() => {
    const cached = sessionStorage.getItem("summaryCache");
    return cached && cached !== "undefined" ? JSON.parse(cached) : {};
  });
  const [isSummaryEmpty, setIsSummaryEmpty] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(() => {
    const cached = sessionStorage.getItem("lastEvaluatedKey");
    return cached && cached !== "undefined" ? JSON.parse(cached) : null;
  });
  const [isEndOfData, setIsEndOfData] = useState(
    sessionStorage.getItem("isEndOfData") === "true"
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [rawSummaries, setRawSummaries] = useState(() => {
    const cached = sessionStorage.getItem("rawSummaries");
    return cached && cached !== "undefined" ? JSON.parse(cached) : [];
  });

  const onViewMoreClick = (summaryData) => {
    console.log("Navigating to detail with fromPage:", currentPage);

    navigate(`/client/summary/${summaryData.aimsUuid}`, {
      state: {
        summary: summaryData,
        fromPage: currentPage,
      },
    });
  };

  // Function to load more summaries when clicking next on the last page
  const loadMoreSummaries = async () => {
    if (isLoadingMore || isEndOfData) return;
    
    setIsLoadingMore(true);
    try {
      // Fetch more summaries
      const result = await fetchPaginatedSummaries(
        false, 
        lastEvaluatedKey, 
        10,
        {
          setSummaryGroups,
          setRawSummaries,
          setLastEvaluatedKey,
          setIsEndOfData
        }
      );
      
      if (result.summaryListJson.length === 0) {
        setIsLoadingMore(false);
        return;
      }
      
      // Get updated groups and pages
      const updatedGroups = JSON.parse(sessionStorage.getItem("summaryCache") || "{}");
      const newProcessedPages = preProcessInClient(updatedGroups);
      setPages(newProcessedPages);
    } catch (error) {
      LogManager.error("Failed to fetch more summaries:", error);
      setShowServerError(true);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Check if we have data in the cache and set initial state
  useEffect(() => {
    const returnPage = props.returnPage || location.state?.returnPage;
    const hasCachedData = Object.keys(summaryGroups).length > 0;
    if (window.location.href.includes("http://localhost:3000/")) {
      const groups = generateSummaryGroup(sampleSummaryList);
      setSummaryGroups(groups);
      sessionStorage.setItem("summaryCache", JSON.stringify(groups));
      const processedPages = preProcessInClient(groups);
      setPages(processedPages);
    }
    
    // Set initial state based on whether we have data
    setIsSummaryEmpty(!hasCachedData && window.location.href !== "http://localhost:3000/");
    setIsLoading(false);

    // Handle return page
    if (returnPage && returnPage >= 1) {
      setCurrentPage(returnPage);
    } else {
      setCurrentPage(1);
    }
  }, []);

  // Update component when prefetch completes and fresh data is available
  useEffect(() => {
    if (props.isPrefetchComplete && window.location.href !== "http://localhost:3000/") {
      // Get the latest cached data
      const cached = sessionStorage.getItem("summaryCache");
      if (cached && cached !== "undefined") {
        const groups = JSON.parse(cached);
        setSummaryGroups(groups);
        const processedPages = preProcessInClient(groups);
        setPages(processedPages);
        setIsSummaryEmpty(Object.keys(groups).length === 0);
      }
    }
  }, [props.isPrefetchComplete]);

  useEffect(() => {
    const hasSummaries = Object.keys(summaryGroups).length > 0;
    setIsSummaryEmpty(!hasSummaries);
  }, [summaryGroups]);

  // Show loading while waiting for prefetch to complete
  if (!props.isPrefetchComplete) return <Loading />;
  
  if (isLoading && !isLoadingMore) return <Loading />;
  if (showServerError) {
    return <CommonErrorModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />;
  }

  if (isSummaryEmpty || pages.length === 0) {
    return (
      <Column spacing="none" spacingInset="none" alignment="top" alignmentHorizontal="start">
        <Column height="96px" />

        <Row spacing="none" spacingInset="none">
          <Row width="184px" />
          <NoSummary />
        </Row>
      </Column>
    );
  }

  const { groupTitle, summaries } = pages[currentPage - 1];

  return (
    <Column spacing="none" spacingInset="none" alignmentVertical="top" alignmentHorizontal="start">
      <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
        {/*Date box*/}

        <Box type="outline" spacing="none" spacingInset="none" minHeight={68} minWidth={306}>
          <Column spacing="none" spacingInset="none">
            <Column height="32px" />
            <Row>
              <Row width={32} />
              <Text fontFamily="amazonEmber" type="h100">
                {groupTitle}
              </Text>
            </Row>
          </Column>
        </Box>

        {/* Summary Cards */}
        <Box type="outline" spacing="none" spacingInset="none" minHeight={362} width="660px">
          <Row spacing="none" spacingInset="none">
            <Row width="16px" />
            <Column spacing="none" spacingInset="none">
              {[0, 1].map((rowIndex) => (
                <Row key={rowIndex} spacing="none" spacingInset="none">
                  {[0, 1].map((colIndex) => {
                    const index = rowIndex * 2 + colIndex;
                    const summary = summaries[index];
                    if (!summary) return null;
                    return (
                      <Box key={summary.aimsUuid} spacingInset="300">
                        <SummaryCard summary={summary} onViewMoreClick={onViewMoreClick} />
                      </Box>
                    );
                  })}
                </Row>
              ))}
            </Column>
          </Row>
        </Box>

        <Column height="16px" />

        {/*Pagination Box*/}
        <Box type="outline" spacing="none" spacingInset="none" height="40px" width="660px">
          <Row spacing="none" spacingInset="none" alignmentHorizontal="end">
            {isLoadingMore &&
              <Theme palette={aimsTheme}>
                <Loader size="small" />
              </Theme>
            }
            <Pagination 
              currentPageIndex={currentPage}
              pagesCount={pages.length}
              onChange={({ detail }) => {
                // Ensure currentPage is valid
                if (detail.currentPageIndex <= pages.length) {
                  setCurrentPage(detail.currentPageIndex);
                }
                // Check if we need to load more summaries
                if (detail.currentPageIndex == pages.length && !isEndOfData) {
                  loadMoreSummaries();
                }
              }}
              openEnd
            />
          </Row>
        </Box>
      </Theme>
    </Column>
  );
};

export default SummariesTabInClient;
